import React, { useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import PlannedMeal from './PlannedMeal.component';
import { getStockLevels } from '../../actions/meal_stock.actions';

const useStyles = makeStyles((theme) => ({
  // Persistent (non-modal) so meals can be dragged out onto the planner, and sit
  // in the normal page stacking rather than floating above everything as a modal.
  paper: { width: 320, zIndex: theme.zIndex.appBar - 1 },
  // Spacer so the drawer content clears the fixed top AppBar (same trick App.js uses).
  toolbar: theme.mixins.toolbar,
  panel: { padding: theme.spacing(1, 2) },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  row: { marginBottom: theme.spacing(1) },
  line: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
  qty: { fontWeight: 'bold' },
  date: { fontSize: '0.7rem', color: '#888' },
  empty: { color: '#888', margin: theme.spacing(1, 0) }
}));

const fmtDate = d => {
  if (!d) return 'never made';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? String(d) : `made ${dt.toLocaleDateString()}`;
};

const noop = () => {};

const StockDrawer = ({ open, onClose }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const stock = useSelector(state => state.stock);

  // Stock is time-sensitive — refresh each time the drawer is opened.
  useEffect(() => {
    if (open) dispatch(getStockLevels());
  }, [open, dispatch]);

  const inStock = (stock.records || [])
    .filter(r => r.currentStock > 0)
    .sort((a, b) => {
      if (!a.lastMade && !b.lastMade) return 0;
      if (!a.lastMade) return 1;
      if (!b.lastMade) return -1;
      return new Date(a.lastMade) - new Date(b.lastMade);
    });

  return (
    <Drawer variant="persistent" anchor="right" open={open} classes={{ paper: classes.paper }}>
      <div className={classes.toolbar} />
      <div className={classes.panel}>
        <div className={classes.header}>
          <Typography variant="h6">In stock (oldest first)</Typography>
          <IconButton size="small" onClick={onClose} aria-label="close stock drawer">
            <ChevronRightIcon />
          </IconButton>
        </div>
        <Divider />
        {inStock.length === 0 &&
          <Typography variant="body2" className={classes.empty}>Nothing in stock.</Typography>}
        {inStock.map(r => (
          <div key={r.mealId} className={classes.row}>
            <div className={classes.line}>
              <PlannedMeal
                plan={{ id: 'stock-' + r.mealId, mealId: r.mealId }}
                onClick={noop}
                onEditMealClick={() => noop}
                showEdit={false}
                draggable={true}
                dragData={JSON.stringify({ type: 'stock', mealId: r.mealId })}
              />
              <span className={classes.qty}>({r.currentStock})</span>
            </div>
            <div className={classes.date}>{fmtDate(r.lastMade)}</div>
          </div>
        ))}
      </div>
    </Drawer>
  );
};

export default StockDrawer;
